/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { css, html, LitElement } from 'lit';
import { cache } from 'lit/directives/cache.js';
import { baseStyles, globalStyle, LogService, notProductionEnvironment, querySlot } from '@cds/core/internal';
import styles from './pagination.element.scss';
/**
 * Web component pagination.
 *
 * ```typescript
 * import '@cds/core/pagination/register.js';
 * ```
 *
 * ```html
 * <cds-pagination>
 *   <cds-pagination-button action="first" disabled></cds-pagination-button>
 *   <cds-pagination-button action="prev" disabled></cds-pagination-button>
 *   <span aria-label="current page">1 / 3</span>
 *   <cds-pagination-button action="next"></cds-pagination-button>
 *   <cds-pagination-button action="last"></cds-pagination-button>
 * </cds-pagination>
 * ```
 *
 * @beta
 * @element cds-pagination
 * @slot
 * @slot cds-pagination-button
 * @cssprop --color
 * @cssprop --font-size
 */
export class CdsPagination extends LitElement {
    constructor() {
        super(...arguments);
        this.globalStyles = css `
    [cds-pagination-number] > cds-control-message {
      --min-width: initial;
    }
  `;
    }
    render() {
        const userLayout = html `<slot></slot>`;
        const defaultLayout = html `<div cds-layout="horizontal gap:sm align:center wrap:none"><slot></slot></div>`;
        return html `${cache(this.hasAttribute('cds-layout') ? userLayout : defaultLayout)}`;
    }
    static get styles() {
        return [baseStyles, styles];
    }
    firstUpdated(props) {
        super.firstUpdated(props);
        this.setupNumberInput();
        this.validateAriaLabel();
    }
    setupNumberInput() {
        if (this.numberInput) {
            this.numberInput.controlWidth = 'shrink';
            this.numberInput.layout = 'compact';
        }
    }
    validateAriaLabel() {
        if (notProductionEnvironment() && !this.getAttribute('aria-label')?.length) {
            LogService.warn('An aria-label is missing', this);
        }
    }
}
__decorate([
    globalStyle()
], CdsPagination.prototype, "globalStyles", void 0);
__decorate([
    querySlot('cds-input[cds-pagination-number]')
], CdsPagination.prototype, "numberInput", void 0);
